import { joiResolver } from '@hookform/resolvers/joi';
import {
  DialogTitle,
  Dialog,
  CircularProgress,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  Skeleton,
} from '@mui/material';
import {
  DateRangeValidationError,
  LocalizationProvider,
} from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useLocalizedJoi from '@/components/hooks/useLocalizedJoi';
import { useSnackbar } from '@/components/hooks/useSnackbar';
import {
  useDownloadCsvByRoomIdLazyQuery,
  useDeleteChatMessagesByRoomIdMutation,
} from '@/types/graphql';
import {
  dateRangePickerCustmize,
  Shortcut,
  shortcutItems,
} from '@/utils/chart';

type LogData = {
  startedAt: string;
  endedAt: string;
};

const DownloadCsvDialogByRoomId = (props: {
  open: boolean;
  onClose: () => void;
  roomId: string;
}) => {
  const { t } = useTranslation('admin');
  const { t: commonT } = useTranslation('common');

  const [rangeError, setRangeError] = useState<DateRangeValidationError>([
    null,
    null,
  ]);
  const hasError = rangeError.some((v) => v != null);

  const { open, onClose, roomId } = props;
  const [getUrl, { data, error, loading, refetch }] =
    useDownloadCsvByRoomIdLazyQuery();
  const [deleteChatMessages, { loading: deleteLoading }] =
    useDeleteChatMessagesByRoomIdMutation();

  const snackbar = useSnackbar();

  const joi = useLocalizedJoi();
  const form = useForm<LogData>({
    resolver: joiResolver(
      joi.object<LogData>({
        startedAt: joi.date().required(),
        endedAt: joi.date().required().greater(joi.ref('startedAt')),
      })
    ),
    defaultValues: {
      startedAt: dayjs().subtract(7, 'day').startOf('day').toISOString(),
      endedAt: dayjs().endOf('day').toISOString(),
    },
  });

  const startedAt = form.watch('startedAt');
  const endedAt = form.watch('endedAt');

  // ダイアログが開いたとき、または日付が変更されたときにデータを取得
  useEffect(() => {
    if (open) {
      // 日付が未指定の場合はundefinedを送信（プロパティ自体を含めない）
      const input = { roomId, startedAt, endedAt };
      getUrl({
        variables: {
          input,
        },
      });
    }
  }, [open, roomId, startedAt, endedAt, getUrl]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('room.downloadLogs')}</DialogTitle>
      <DialogContent>
        <Stack alignItems={'center'} my={1}>
          <Controller
            name="startedAt"
            control={form.control}
            render={() => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateRangePicker
                  {...dateRangePickerCustmize}
                  sx={{ flex: 1 }}
                  value={[
                    startedAt ? dayjs(startedAt) : null,
                    endedAt ? dayjs(endedAt) : null,
                  ]}
                  onError={(error) => setRangeError(error)}
                  onChange={(value, context) => {
                    const shortcut = Object.entries(shortcutItems).find(
                      ([_, item]) => item.label === context.shortcut?.label
                    )?.[0] as Shortcut;
                    if (shortcut && shortcut !== 'custom') {
                      const [start, end] = shortcutItems[shortcut].getValue();
                      form.setValue('startedAt', start.toISOString());
                      form.setValue('endedAt', end.toISOString());
                    } else {
                      if (value[0]) {
                        form.setValue(
                          'startedAt',
                          value[0].startOf('day').toISOString()
                        );
                      }
                      if (value[1]) {
                        form.setValue(
                          'endedAt',
                          value[1].endOf('day').toISOString()
                        );
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Stack>
        <Stack alignItems={'center'}>
          {/* 会話ログ数 ChatMessage */}
          <Stack direction={'row'} spacing={0.5}>
            <Typography>{`${t('room.chatMessageCount')}:`}</Typography>
            {loading ? (
              <Skeleton variant="text" width={20} />
            ) : (
              <Typography>
                {data?.downloadCsvByRoomId.chatMessageCount ?? 0}
              </Typography>
            )}
          </Stack>
          {/* 累計利用者数 ChatSessionUser */}
          <Stack direction={'row'} spacing={0.5}>
            <Typography>{`${t('room.chatSessionUserCount')}:`}</Typography>
            {loading ? (
              <Skeleton variant="text" width={20} />
            ) : (
              <Typography>
                {data?.downloadCsvByRoomId.chatSessionUserCount ?? 0}
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center' }}>
        {loading && (
          <CircularProgress size={24} sx={{ position: 'absolute' }} />
        )}
        <Button type="button" onClick={onClose} variant="outlined">
          {commonT('command.cancel')}
        </Button>
        <Button
          color="error"
          disabled={deleteLoading || hasError}
          onClick={async () => {
            try {
              await deleteChatMessages({
                variables: {
                  input: {
                    roomId,
                    startedAt: form.getValues('startedAt') || undefined,
                    endedAt: form.getValues('endedAt') || undefined,
                  },
                },
              });

              snackbar('success', commonT('message.removed'));

              // データを再取得
              await refetch();
            } catch (error) {
              console.error('removeError:', error);
            }
          }}
        >
          {deleteLoading
            ? `${t('room.deleting')}...`
            : commonT('command.delete')}
        </Button>
        <Button
          variant={error ? 'outlined' : 'contained'}
          disabled={
            loading ||
            hasError ||
            !data?.downloadCsvByRoomId.downloadUrl ||
            data.downloadCsvByRoomId.downloadUrl === '' ||
            (data.downloadCsvByRoomId.chatMessageCount ?? 0) < 1
          }
          onClick={() => {
            const url = data?.downloadCsvByRoomId.downloadUrl;
            if (url && url !== '') {
              // 動的にaタグを作成してダウンロード
              const link = document.createElement('a');
              link.href = url;
              link.download = ''; // ファイル名はS3のURLから自動取得
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              onClose();
            }
          }}
        >
          {error
            ? t('room.noLogsMsg')
            : loading
            ? commonT('message.wating')
            : t('room.startDownloadLogs')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadCsvDialogByRoomId;
